import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import adminModule from "../../../api/modules/admin.module";
import { showToast } from "../../../utils/toast";
import SystemSettingsView from "./SystemSettings.view";
import "./SystemSettings.css";

/**
 * /admin-system-settings, the container. The query, the mutation, the toasts.
 *
 * Everything visual lives in `SystemSettings.view.jsx`, which is pure, so the
 * state explorer can mount loading, loaded, saving and failed without the API.
 *
 * The `AdminLayout` wrapper went: `ConsoleShell variant="admin"` is the real
 * chrome and it is the same shell every other admin route mounts in.
 *
 * NOTE ON THE QUERY: `queryFn` calls `setState` as a side effect, which is how
 * the original seeded the two controls. That is preserved rather than rewritten,
 * because changing it changes when the fields populate, and this batch is a
 * conversion. It is worth revisiting: a `queryFn` that writes component state
 * runs again on every refetch and will overwrite an edit in progress.
 */

const SystemSettings = () => {
  const queryClient = useQueryClient();
  const [platformFee, setPlatformFee] = useState(5);
  const [sellInPerson, setSellInPerson] = useState(false);

  const { isLoading, error } = useQuery({
    queryKey: ["systemSettings"],
    queryFn: async () => {
      const result = await adminModule.getSystemSettings();
      if (result.success) {
        setPlatformFee(result.data?.platform_fee || 5);
        setSellInPerson(result.data?.sell_in_person || false);
        return result.data;
      }
      throw new Error(result.message);
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (settings) => adminModule.updateSystemSettings(settings),
    onSuccess: (result) => {
      if (result.success) {
        showToast.success(result.message || "Settings updated successfully");
        queryClient.invalidateQueries(["systemSettings"]);
      } else {
        showToast.error(result.message || "Failed to update settings");
      }
    },
    onError: (mutationError) => {
      showToast.error(mutationError.message || "Failed to update settings");
    },
  });

  const handleSave = () => {
    if (platformFee < 0 || platformFee > 100) {
      showToast.error("Platform fee must be between 0 and 100");
      return;
    }

    updateSettingsMutation.mutate({
      platform_fee: parseFloat(platformFee),
      sell_in_person: sellInPerson,
    });
  };

  return (
    <SystemSettingsView
      platformFee={platformFee}
      onPlatformFeeChange={setPlatformFee}
      sellInPerson={sellInPerson}
      onSellInPersonChange={setSellInPerson}
      loading={isLoading}
      saving={updateSettingsMutation.isPending}
      error={error?.message}
      onSave={handleSave}
    />
  );
};

export default SystemSettings;
